"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Mail, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { isLiveSite } from "@/lib/pageLinks";
const FOOTER_LOGO = "https://tektideslabs.com/wp-content/uploads/2026/09/Logo-Real.png";
const legalLinks = [{
  to: "/PoliciesPage#terms-of-service",
  hash: "#terms-of-service",
  label: "Terms & Conditions"
}, {
  to: "/PoliciesPage#privacy-policy",
  hash: "#privacy-policy",
  label: "Privacy Policy"
}, {
  to: "/PoliciesPage#shipping-policy",
  hash: "#shipping-policy",
  label: "Shipping Policy"
}, {
  to: "/PoliciesPage#refund-policy",
  hash: "#refund-policy",
  label: "Returns & Refunds"
}, {
  to: "/PoliciesPage#research-use-policy",
  hash: "#research-use-policy",
  label: "Research Use Policy"
}, {
  to: "/PoliciesPage#legal-compliance-toc",
  hash: "#legal-compliance-toc",
  label: "All Policies"
}];
interface FallbackNavItem {
  label: string;
  linkProps: {
    to?: string;
    href?: string;
  };
}
function getFallbackNavigation(): FallbackNavItem[] {
  const live = isLiveSite();
  return [{
    label: "Products",
    linkProps: live ? {
      href: "/research-products/"
    } : {
      to: "/ShopPage"
    }
  }, {
    label: "About",
    linkProps: live ? {
      href: "/about/"
    } : {
      to: "/AboutPage"
    }
  }, {
    label: "FAQ",
    linkProps: live ? {
      href: "/faq/"
    } : {
      to: "/FaqPage"
    }
  }, {
    label: "Contact",
    linkProps: live ? {
      href: "/contact/"
    } : {
      to: "/ContactPage"
    }
  }, {
    label: "Account",
    linkProps: live ? {
      href: "/my-account/"
    } : {
      to: "/MyAccountPage"
    }
  }];
}
function FooterMenuSubtree({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  if (!items || items.length === 0) {
    return null;
  }
  const live = isLiveSite();
  const defaultDestination = live ? {
    href: "/research-products/"
  } : {
    to: "/ShopPage"
  };
  return <ul className={depth === 0 ? "space-y-3" : "mt-3 space-y-2 border-l border-white/10 pl-3"}>
      {items.map((child, index) => {
      const hasValidHref = Boolean(child.href && child.href !== "#");
      const linkProps = hasValidHref ? {
        href: child.href
      } : defaultDestination;
      const isExternal = typeof child.href === "string" && /^https?:\/\//i.test(child.href);
      return <li key={child.id || `${child.label}-${index}`}>
            <Link {...linkProps} className="group inline-flex items-center gap-1.5 text-sm text-white/55 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#159fdf]">
              <span>{child.label}</span>

              {isExternal && <ArrowUpRight className="size-3 opacity-50 transition-opacity group-hover:opacity-100" aria-hidden="true" />}
            </Link>

            {child.children && child.children.length > 0 && <FooterMenuSubtree items={child.children} depth={depth + 1} />}
          </li>;
    })}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3" aria-label="Loading navigation" aria-busy="true">
        <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
        <div className="h-4 w-32 animate-pulse rounded bg-white/10" />
        <div className="h-4 w-28 animate-pulse rounded bg-white/10" />
        <div className="h-4 w-20 animate-pulse rounded bg-white/10" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackItems = getFallbackNavigation();
    return <ul className="space-y-3">
        {fallbackItems.map(item => <li key={item.label}>
            <Link {...item.linkProps} className="inline-flex text-sm text-white/55 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#159fdf]">
              {item.label}
            </Link>
          </li>)}
      </ul>;
  }
  return <FooterMenuSubtree items={menuItems} />;
}
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const location = useLocation();
  const isPoliciesPage = location.pathname === "/PoliciesPage" || location.pathname.toLowerCase().startsWith("/policies");
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.6,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  return <footer id="footer" data-nav="dark" className="relative overflow-hidden bg-[#0b171c] text-white">
      {/* subtle TEK blue atmosphere */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-48 -top-56 h-[560px] w-[560px] rounded-full bg-[#087db3]/10 blur-[150px]" />

        <div className="absolute -bottom-64 -left-44 h-[500px] w-[500px] rounded-full bg-[#159fdf]/[0.055] blur-[150px]" />

        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#159fdf]/35 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1440px] px-4 pb-8 pt-20 sm:px-6 sm:pt-24 lg:px-8 lg:pt-28">

        {/* -------------------------------------------------------------- */}
        {/* LARGE BRAND LINE                                               */}
        {/* -------------------------------------------------------------- */}

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }}>
          <div className="grid gap-10 border-b border-white/[0.09] pb-16 lg:grid-cols-[1.2fr_0.8fr] lg:items-end lg:pb-20">
            <div>
              <div className="mb-7 flex items-center gap-3">
                <span className="h-px w-8 bg-[#159fdf]" />

                <span className="text-[10px] font-semibold uppercase tracking-[0.23em] text-[#66bfe5]">
                  TEK TIDES LABS
                </span>
              </div>

              <h2 className="max-w-[760px] text-[clamp(3rem,5.8vw,6.8rem)] font-semibold leading-[0.9] tracking-[-0.065em] text-white">
                We rise
                <br />
                with the tide.
              </h2>
            </div>

            <div className="max-w-[390px] lg:justify-self-end lg:pb-2">
              <p className="text-sm leading-7 text-white/50 sm:text-[15px]">
                Research-use products with clear product information,
                accessible documentation, and straightforward support.
              </p>

              <Link to="/ShopPage" className="group mt-7 inline-flex items-center gap-3 text-sm font-semibold text-[#66bfe5] transition-colors hover:text-white">
                Explore Products

                <span className="flex size-9 items-center justify-center rounded-full border border-white/15 transition-all group-hover:border-[#159fdf] group-hover:bg-[#159fdf] group-hover:text-[#0b171c]">
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </Link>
            </div>
          </div>

          {/* ------------------------------------------------------------ */}
          {/* FOOTER CONTENT                                               */}
          {/* ------------------------------------------------------------ */}

          <div className="grid grid-cols-1 gap-12 py-14 md:grid-cols-2 lg:grid-cols-12 lg:gap-8 lg:py-16">

            {/* Brand */}
            <div className="lg:col-span-4">
              <Link to="/" aria-label="TEK TIDES LABS home" className="inline-block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#159fdf]">
                <img src={FOOTER_LOGO} alt="TEK TIDES LABS" loading="lazy" decoding="async" className="h-auto w-full max-w-[230px] object-contain" />
              </Link>

              <p className="mt-6 max-w-[330px] text-sm leading-7 text-white/45">
                Clear research-use product information and support
                from selection through order review.
              </p>

              <div className="mt-6 inline-flex items-center gap-2.5">
                <ShieldCheck className="size-4 text-[#159fdf]" strokeWidth={1.5} aria-hidden="true" />

                <span className="text-[9px] font-semibold uppercase tracking-[0.19em] text-white/35">
                  Research Use Only
                </span>
              </div>
            </div>

            {/* Navigation */}
            <nav className="space-y-6 lg:col-span-2" aria-label="Footer navigation">
              <h2 className="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#66bfe5]">
                Navigation
              </h2>

              <MenuProvider menu_id="21" className="footer-menu">
                <FooterMenuConsumer />
              </MenuProvider>
            </nav>

            {/* Legal */}
            <nav className="space-y-6 lg:col-span-3" aria-label="Legal policies">
              <h2 className="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#66bfe5]">
                Legal
              </h2>

              <ul className="space-y-3">
                {legalLinks.map(link => {
                const destination = isPoliciesPage ? link.hash : link.to;
                return <li key={link.hash}>
                      <Link to={destination} className="inline-block text-sm text-white/55 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#159fdf]">
                        {link.label}
                      </Link>
                    </li>;
              })}
              </ul>
            </nav>

            {/* Contact */}
            <div className="space-y-6 lg:col-span-3">
              <h2 className="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#66bfe5]">
                Contact
              </h2>

              <div className="space-y-6">
                <div>
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.17em] text-white/25">
                    Customer Support
                  </span>

                  <a href="mailto:contact@tektideslabs.com" className="group inline-flex items-center gap-2.5 text-sm text-white/55 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#159fdf]">
                    <Mail className="size-4 shrink-0 text-[#159fdf]" strokeWidth={1.5} aria-hidden="true" />

                    <span className="break-all">
                      contact@tektideslabs.com
                    </span>
                  </a>
                </div>

                <div>
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.17em] text-white/25">
                    Order Support
                  </span>

                  <a href="mailto:orders@tektideslabs.com" className="group inline-flex items-center gap-2.5 text-sm text-white/55 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#159fdf]">
                    <Mail className="size-4 shrink-0 text-[#159fdf]" strokeWidth={1.5} aria-hidden="true" />

                    <span className="break-all">
                      orders@tektideslabs.com
                    </span>
                  </a>
                </div>

                <Link to="/ContactPage" className="group inline-flex items-center gap-2 text-xs font-semibold text-[#66bfe5] transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#159fdf]">
                  Contact TEK TIDES LABS

                  <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        <Separator className="bg-white/[0.09] opacity-100" />

        {/* -------------------------------------------------------------- */}
        {/* BOTTOM                                                         */}
        {/* -------------------------------------------------------------- */}

        <div className="py-8">
          <p className="max-w-[900px] text-[10px] leading-5 text-white/30 sm:text-[11px]">
            Products offered by TEK TIDES LABS are supplied strictly
            for research use only and are not intended for diagnostic,
            clinical, therapeutic, human, or veterinary applications.
            Website information is provided for product identification
            and research-reference purposes only.
          </p>

          <div className="mt-7 flex flex-col gap-4 border-t border-white/[0.07] pt-6 text-[9px] font-medium uppercase tracking-[0.15em] text-white/25 sm:flex-row sm:items-center sm:justify-between">
            <p>
              © 2026 TEK TIDES LABS LLC. All rights reserved.
            </p>

            <p className="text-white/35">
              We Rise With The Tide.
            </p>
          </div>
        </div>
      </div>
    </footer>;
}